import type { ResolvedConfiguratorCoupon } from '$models/OrderConfigurator/ConfiguratorCoupon';
import type { ConfiguratorState } from './configurator.svelte';
import { browser } from '$app/environment';
import { page } from '$app/state';

const TEMP_COUPON_STORAGE_KEY = 'coupon_temp';
const TEMP_COUPON_DURATION_MS = 3600000;

type TemporaryCoupon = {
	code?: unknown;
	expiresAt?: unknown;
};

function getLocalStorage() {
	if (!browser || typeof localStorage === 'undefined') {
		return null;
	}

	return localStorage;
}

function parseTemporaryCoupon(value: string): TemporaryCoupon | null {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
}

export function resolveConfiguratorCoupon(): ResolvedConfiguratorCoupon | null {
	if (!browser) {
		return null;
	}

	const couponFromUrl = page.url.searchParams.get('coupon');
	const storage = getLocalStorage();

	if (couponFromUrl !== null && couponFromUrl.length > 0) {
		storage?.setItem(
			TEMP_COUPON_STORAGE_KEY,
			JSON.stringify({
				code: couponFromUrl,
				expiresAt: Date.now() + TEMP_COUPON_DURATION_MS
			})
		);

		return {
			code: couponFromUrl,
			source: 'url'
		};
	}

	const couponFromLocalStorage = storage?.getItem(TEMP_COUPON_STORAGE_KEY);

	if (!couponFromLocalStorage) {
		return null;
	}

	const tempCoupon = parseTemporaryCoupon(couponFromLocalStorage);

	if (
		typeof tempCoupon?.code === 'string' &&
		tempCoupon.code.length > 0 &&
		typeof tempCoupon.expiresAt === 'number' &&
		tempCoupon.expiresAt > Date.now()
	) {
		return {
			code: tempCoupon.code,
			source: 'temporary'
		};
	}

	storage?.removeItem(TEMP_COUPON_STORAGE_KEY);
	return null;
}

export function applyConfiguratorCoupon(
	configuratorState: ConfiguratorState,
	coupon: ResolvedConfiguratorCoupon | string | null | undefined
) {
	const couponCode = typeof coupon === 'string' ? coupon : coupon?.code;

	if (!couponCode) {
		return false;
	}

	configuratorState.setCouponCodes({
		recurringCode: couponCode,
		onetimeCode: couponCode
	});

	return true;
}

export function resolveAndApplyConfiguratorCoupon(configuratorState: ConfiguratorState) {
	return applyConfiguratorCoupon(configuratorState, resolveConfiguratorCoupon());
}
